'use client'

import React, { ErrorInfo, ReactNode } from 'react'
import { trackError } from '@/lib/analytics'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

class PostHogErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Track error with our enhanced error tracking
    trackError(
      'react_error_boundary',
      error.message,
      'error_boundary',
      undefined, // userEmail will be set by trackError if user is logged in
      {
        error_name: error.name,
        error_stack: error.stack,
        component_stack: errorInfo.componentStack,
        error_boundary: true,
        timestamp: new Date().toISOString()
      }
    );

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by PostHog Error Boundary:', error, errorInfo)
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="error-boundary">
          <h2>Something went wrong.</h2>
          <p>An error occurred while rendering this component.</p>
        </div>
      )
    }

    return this.props.children
  }
}

export default PostHogErrorBoundary